import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { app } from "../firebase";
import {
  PawPrint,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Heart,
  User,
  Info,
  Camera,
} from "lucide-react";

const PetProfile = () => {
  const { petId } = useParams();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const db = getFirestore(app);

  useEffect(() => {
    const loadPetProfile = async () => {
      try {
        setLoading(true);

        // Get pet data from Firestore
        const petDoc = await getDoc(doc(db, "pets", petId));

        if (!petDoc.exists()) {
          setError("Pet not found");
          return;
        }

        const petData = petDoc.data();
        setPet(petData);
      } catch (err) {
        console.error("Error loading pet profile:", err);
        setError("Failed to load pet information");
      } finally {
        setLoading(false);
      }
    };

    if (petId) {
      loadPetProfile();
    }
  }, [petId, db]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading pet information...</p>
        </div>
      </div>
    );
  }

  if (error || !pet) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <PawPrint className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Pet Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error || "The pet you're looking for could not be found."}
          </p>
          <a
            href="https://petid-77a78.web.app"
            className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300"
          >
            Go to PetID Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center">
                <PawPrint className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-white">PetID.Neural</h1>
            </div>
            <div className="text-sm text-gray-400">
              Found a pet? Contact the owner below
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alert Banner */}
        <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-500/30 rounded-lg p-4 mb-8">
          <div className="flex items-center space-x-3">
            <Heart className="w-6 h-6 text-green-400" />
            <div>
              <h2 className="text-lg font-bold text-green-400">
                🎉 You Found {pet.name}!
              </h2>
              <p className="text-gray-300">
                This pet is registered and loved. Please contact the owner using
                the information below.
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Pet Photo and Basic Info */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-center">
              {pet.photos && pet.photos.length > 0 ? (
                <div className="w-48 h-48 rounded-lg overflow-hidden mx-auto mb-6 border-2 border-cyan-500/30">
                  <img
                    src={
                      pet.photos[0].startsWith("data:")
                        ? pet.photos[0]
                        : `data:image/jpeg;base64,${pet.photos[0]}`
                    }
                    alt={pet.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-48 h-48 rounded-lg bg-gray-700 flex items-center justify-center mx-auto mb-6 border-2 border-gray-600">
                  <Camera className="w-16 h-16 text-gray-400" />
                </div>
              )}

              <h1 className="text-3xl font-bold text-white mb-2">{pet.name}</h1>
              <p className="text-xl text-gray-300 mb-4">
                {pet.species} • {pet.breed}
              </p>

              {pet.age && (
                <p className="text-gray-400 mb-4">Age: {pet.age} years old</p>
              )}

              {pet.description && (
                <div className="bg-gray-700 rounded-lg p-4 mb-4">
                  <h3 className="text-white font-medium mb-2 flex items-center">
                    <Info className="w-4 h-4 mr-2" />
                    About {pet.name}
                  </h3>
                  <p className="text-gray-300 text-sm">{pet.description}</p>
                </div>
              )}

              <div className="bg-gray-700 rounded-lg p-3">
                <p className="text-cyan-400 font-mono text-sm">
                  Pet ID: {pet.id}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <User className="w-6 h-6 text-cyan-400 mr-2" />
                Contact Owner
              </h2>

              <div className="space-y-4">
                {pet.contactInfo?.phone && (
                  <div className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                    <Phone className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-gray-400 text-sm">Phone</p>
                      <a
                        href={`tel:${pet.contactInfo.phone}`}
                        className="text-green-400 hover:text-green-300 font-medium"
                      >
                        {pet.contactInfo.phone}
                      </a>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <a
                      href="https://petid-77a78.web.app"
                      className="text-blue-400 hover:text-blue-300 font-medium"
                    >
                      Contact via PetID App
                    </a>
                  </div>
                </div>

                {pet.createdAt && (
                  <div className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                    <Calendar className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-gray-400 text-sm">Registered</p>
                      <p className="text-purple-400 font-medium">
                        {new Date(
                          pet.createdAt.seconds * 1000
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-gradient-to-r from-cyan-500/10 to-purple-600/10 border border-cyan-500/30 rounded-lg p-6">
              <h3 className="text-lg font-bold text-white mb-3">
                How to Help {pet.name}
              </h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start space-x-2">
                  <span className="text-cyan-400 font-bold">1.</span>
                  <span>Call the phone number above if available</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-cyan-400 font-bold">2.</span>
                  <span>Keep {pet.name} safe and comfortable</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-cyan-400 font-bold">3.</span>
                  <span>Take a photo of where you found {pet.name}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-cyan-400 font-bold">4.</span>
                  <span>Contact local animal services if needed</span>
                </li>
              </ul>
            </div>

            {/* App Download */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <h3 className="text-lg font-bold text-white mb-3">
                Get the PetID App
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Register your own pets and help reunite lost pets with their
                families
              </p>
              <a
                href="https://petid-77a78.web.app"
                className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 inline-block"
              >
                Visit PetID.Neural
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetProfile;
